import React, { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import objectSupport from 'dayjs/plugin/objectSupport'
import isBetween from 'dayjs/plugin/isBetween'
import { Table, Row, Col } from 'antd'
import { useLocalStorage } from 'react-use'
import { apiGetFaults } from '../api/faults'
import { useUserPlant, useUserTestZones, useUserInfo } from '../hooks/User'
import { ReactComponent as ComponentIcon } from '../assets/images/icons/svg/component-c-circle.svg'
import { ReactComponent as DeviceIcon } from '../assets/images/icons/svg/device-d-circle.svg'
import { ReactComponent as HarnessIcon } from '../assets/images/icons/svg/harness-d-circle.svg'
import {
  navigateToVehicleTest,
  openTabWithVehicleTestByResultId,
} from '../api/vehicle-tests-api'

import { useFaults } from '../hooks/Faults'
import { useShifts } from '../hooks/Shifts'
import { useGetUserTestZones } from '../hooks/TestZones'
import { useSelector } from 'react-redux'
import {
  dispatchEvent,
  addPageAttribute,
  removePageAttribute,
} from '../CloudWatch-init'

dayjs.extend(objectSupport)
dayjs.extend(utc)
dayjs.extend(isBetween)

const FAULT_TYPE_ICONS = {
  component: <ComponentIcon />,
  device: <DeviceIcon />,
  harness: <HarnessIcon />,
}

export function MonitorTable({
  refreshInterval,
  searchTerm,
  sessionId,
  onPreviousShiftComputed,
}) {
  // Track time spent on the page
  const timeOnMonitorTable = React.useRef(null)
  // Get user info and plant from hooks
  const { userInfo } = useUserInfo()
  const [userPlant] = useUserPlant()
  const userName = userInfo?.username || ''

  const sendEventUpdateTimeOnPage = () => {
    if (timeOnMonitorTable.current) {
      console.log('sending the following usage event:', {
        searchTerm,
        timeOnMonitorTable: Date.now() - timeOnMonitorTable.current,
        plant: userPlant,
        session_id: sessionId,
      })
      dispatchEvent &&
        dispatchEvent('MONITOR_PAGE_TIME_SPENT', {
          searchTerm,
          timeOnMonitorTable: Date.now() - timeOnMonitorTable.current,
          plant: userPlant,
          session_id: sessionId,
        })
    }
  }

  useEffect(() => {
    const eventIntervalId = setInterval(() => {
      sendEventUpdateTimeOnPage()
    }, 900000) // Send an event every 15 minutes with updated time on page

    return () => {
      clearInterval(eventIntervalId)
    }
  }, [])

  // Log usage event on mount and set page/session attributes
  useEffect(() => {
    timeOnMonitorTable.current = Date.now()
    console.log(
      'dispatching MONITOR_PAGE_LOADED with searchTerm:',
      searchTerm,
      'and plant:',
      userPlant,
      'and session_id:',
      sessionId,
    )
    dispatchEvent &&
      dispatchEvent('MONITOR_PAGE_LOADED', {
        searchTerm,
        plant: userPlant,
        session_id: sessionId,
      })

    // Only set static session info as page/session attributes
    const userDepartment = searchTerm?.department || ''
    addPageAttribute({
      monitorTableUser: userName,
      monitorTablePlant: userPlant,
      monitorTableDepartment: userDepartment,
    })

    return () => {
      sendEventUpdateTimeOnPage()
      // Remove page/session attributes
      removePageAttribute('monitorTableUser')
      removePageAttribute('monitorTablePlant')
      removePageAttribute('monitorTableDepartment')
    }
  }, [userName, userPlant, searchTerm])

  const [plant] = useUserPlant()
  const [testZones] = useUserTestZones()
  const [faults, setFaults] = React.useState([])
  const [testZonesBySpecifier, setTestZonesBySpecifier] = useState({})
  const { isLoading: testZonesLoading, allTestZones } = useGetUserTestZones()
  useEffect(() => {
    if (testZonesLoading || !plant) {
      return
    }
    const tzBySpecifier = {}
    allTestZones.forEach((tz) => {
      if (tz.plant.name.toUpperCase() === plant.toUpperCase()) {
        tzBySpecifier[tz.specifier] = tz
      }
    })
    setTestZonesBySpecifier(tzBySpecifier)
  }, [allTestZones, testZonesLoading, plant])
  const [station_alerts] = useLocalStorage('station-alert', 'send,skip')
  const { isLoading: shiftsIsLoading, shifts: plantShifts } = useShifts({
    plant_name: plant,
  })

  const getShiftTimesToday = (shift) => {
    // shifts are specified like {starts_at: '08:00', ends_at: '16:00'}
    // times are in utc
    // returns the same object with dayjs objects for start and end,
    // adjusted to the current day
    const today = dayjs().utc()
    const start = dayjs.utc(shift.starts_at, 'HH:mm')
    const startToday = dayjs.utc({
      year: today.year(),
      month: today.month(),
      date: today.date(),
      hour: start.hour(),
      minute: start.minute(),
      second: start.second(),
    })
    const end = dayjs.utc(shift.ends_at, 'HH:mm')
    const endToday = dayjs.utc({
      year: today.year(),
      month: today.month(),
      date: today.date(),
      hour: end.hour(),
      minute: end.minute(),
      second: end.second(),
    })
    return {
      ...shift,
      starts_at: startToday,
      ends_at: endToday,
    }
  }

  useEffect(() => {
    if (shiftsIsLoading) {
      return
    }
    // set the current shift as searchTerm.period.start and searchTerm.period.end
    // plant shifts are objects with starts_at and ends_at properties in utc
    // we want to find the one we're currently in. A shift may go past the boundary of a day
    const now = dayjs().utc()
    let shiftToday = null
    let currentShiftIndex = -1
    const resolvedShifts = []
    for (let i = 0; i < plantShifts.length; i++) {
      const shift = plantShifts[i]
      shiftToday = getShiftTimesToday(shift)
      let shiftStart = shiftToday.starts_at
      let shiftEnd = shiftToday.ends_at
      let overlapsDayBoundary = false
      if (shiftEnd < shiftStart) {
        // shift goes past midnight, so we need to adjust the end time
        overlapsDayBoundary = true
      }
      if (overlapsDayBoundary) {
        const dayStart = dayjs().startOf('day')
        const dayEnd = dayjs().endOf('day')
        if (now.isBetween(shiftStart, dayEnd)) {
          // shift started today, ends tomorrow
          searchTerm.period.start = searchTerm.start_at =
            shiftStart.toISOString()
          shiftEnd = shiftEnd.add(1, 'day')
          searchTerm.period.end = searchTerm.end_at = shiftEnd.toISOString()
          currentShiftIndex = i
          resolvedShifts.push({ starts_at: shiftStart, ends_at: shiftEnd })
          break
        } else if (now.isBetween(dayStart, shiftEnd)) {
          // shift started yesterday, ends today
          shiftStart = shiftStart.subtract(1, 'day')
          searchTerm.period.start = searchTerm.start_at =
            shiftStart.toISOString()
          searchTerm.period.end = searchTerm.end_at = shiftEnd.toISOString()
          currentShiftIndex = i
          resolvedShifts.push({ starts_at: shiftStart, ends_at: shiftEnd })
          break
        }
      } else if (now.isBetween(shiftStart, shiftEnd)) {
        searchTerm.period.start = searchTerm.start_at = shiftStart.toISOString()
        searchTerm.period.end = searchTerm.end_at = shiftEnd.toISOString()
        currentShiftIndex = i
        resolvedShifts.push({ starts_at: shiftStart, ends_at: shiftEnd })
        break
      }
    }

    // Compute previous shift times
    if (currentShiftIndex >= 0 && onPreviousShiftComputed) {
      const currentStart = dayjs(searchTerm.period.start)
      // The previous shift is the one whose end time matches the current shift's start time
      for (let i = 0; i < plantShifts.length; i++) {
        const candidate = getShiftTimesToday(plantShifts[i])
        let candStart = candidate.starts_at
        let candEnd = candidate.ends_at
        if (candEnd.isBefore(candStart)) {
          // Shift crosses midnight — start is on the previous day
          candStart = candStart.subtract(1, 'day')
        }
        // If end is after currentStart, this candidate is pinned too late — shift back a day
        if (candEnd.isAfter(currentStart)) {
          candStart = candStart.subtract(1, 'day')
          candEnd = candEnd.subtract(1, 'day')
        }
        if (candEnd.isSame(currentStart)) {
          onPreviousShiftComputed({
            start_at: candStart.toISOString(),
            end_at: candEnd.toISOString(),
          })
          break
        }
      }
    }
  }, [plantShifts, shiftsIsLoading])
  const {
    status,
    faults: fetchedFaults,
    isLoading,
    refetch,
  } = useFaults({
    makeModelIds: searchTerm.makeModelIds || [],
    stationIds: searchTerm.stationIds || [],
    start_at: searchTerm.period.start,
    end_at: searchTerm.period.end,
    zones: searchTerm.zones,
    teams: searchTerm.teams,
    department: searchTerm.department,
    testZones: testZones ? testZones.map((tz) => tz.specifier) : [],
    plants: plant,
    station_alerts,
    reason: searchTerm.reason,
    vinStatus: searchTerm.vinStatus,
    refreshInterval,
  })

  React.useEffect(() => {
    if (fetchedFaults) {
      setFaults([]) // Clear previous data before loading new data, if we don't do this the new stuff isn't expanded
      setFaults(fetchedFaults)
    }
  }, [fetchedFaults])

  const columns = [
    {
      title: 'VIN',
      dataIndex: 'vin',
      key: 'vin',
      render: (vin, fault) => {
        return (
          <a
            onClick={() => {
              // Navigate to the vehicle test details page
              openTabWithVehicleTestByResultId({
                vehicleTestResultId: fault.vehicle_test_result_id,
                componentId:
                  fault.component_id || fault.harness || fault.device_name,
              })
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                openTabWithVehicleTestByResultId({
                  vehicleTestResultId: fault.vehicle_test_result_id,
                  componentId:
                    fault.component_id || fault.harness || fault.device_name,
                })
              }
            }}
          >
            {vin.slice(0, -8)}&nbsp;&nbsp;{vin.slice(-8)}
          </a>
        )
      },
    },
    {
      title: 'Sequence Number',
      dataIndex: 'sequence_number',
      key: 'sequence_number',
      sorter: (a, b) => a.sequence_number - b.sequence_number,
      sortDirections: ['ascend', 'descend'],
      defaultSortOrder: 'descend',
    },
    {
      title: 'Make Model',
      dataIndex: 'model_description',
      key: 'model_description',
      render: (modelDescription) =>
        modelDescription ? modelDescription.toUpperCase() : '',
    },
    {
      title: 'Component',
      render: (fault) => {
        if (fault.type === 'component') {
          return `${fault.component_id} ${
            fault.translated_description
              ? fault.translated_description
              : fault.component_description
          }`
        }
        if (fault.type === 'harness') {
          return `${fault.harness} ${
            fault.translated_description
              ? fault.translated_description
              : fault.description
          }`
        }
        if (fault.type === 'device') {
          const description = fault.translated_description
            ? fault.translated_description
            : fault.description
              ? fault.description
              : ''
          return `${fault.device_name} ${description}`
        }
      },
    },
    {
      title: 'Root Cause',
      dataIndex: 'fault_key',
      key: 'fault_key',
      render: (faultKey, record) => {
        return faultKey === '' ? record.reason : faultKey
      },
    },
    {
      title: 'Time Stamp',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (tmstamp) => {
        return dayjs(tmstamp).utc().local().format('MM/DD/YYYY h:mma')
      },
    },
    {
      title: 'Vin Status',
      dataIndex: 'vin_status',
      key: 'vin_status',
    },
    {
      title: 'Station',
      dataIndex: 'station_id',
      key: 'station_id',
    },
    {
      title: 'Test Zone',
      dataIndex: 'hardware_location_id',
      key: 'hardware_location_id',
      render: (hardware_location_id) => {
        if (testZonesBySpecifier.hasOwnProperty(hardware_location_id)) {
          const tz = testZonesBySpecifier[hardware_location_id]
          return tz.description ? tz.description : tz.specifier
        }
        return hardware_location_id
      },
    },
    {
      title: 'User ID',
      dataIndex: 'created_by',
      key: 'created_by',
    },
  ]

  const expandable = {
    expandedRowRender: (fault) => {
      return (
        <Row align="middle" className="expanded-fault-details">
          <Col span={1} className="fault-type-icon">
            {FAULT_TYPE_ICONS[fault.type] || fault.type}
          </Col>
          <Col span={4} className="fault-description">
            {fault.type === 'component'
              ? fault.component_description
              : fault.description}
          </Col>
          <Col span={2} className="fault-note">
            {fault.note}
          </Col>
          <Col span={1} className="num-dtcs">
            {fault.DTCs.length} DTCs
          </Col>
          <Col span={16} className="fault-dtc-list">
            <ul className="fault-dtcs-info">
              {fault.DTCs && fault.DTCs.length > 0
                ? fault.DTCs.map((dtc) => {
                    return (
                      <li>
                        <span className="dtc-code">{dtc.dtc}</span>
                        <span className="dtc-description">
                          - {dtc.short_desc}{' '}
                        </span>
                      </li>
                    )
                  })
                : ''}
            </ul>
          </Col>
        </Row>
      )
    },
  }

  return (
    <div id="monitor-table-container">
      {faults.length > 0 && (
        <Table
          columns={columns}
          dataSource={faults}
          rowKey="id"
          pagination={false}
          expandable={expandable}
        />
      )}
    </div>
  )
}
